import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Loader2, Plus, Search, Lightbulb, Layout, BookOpen, Inbox, X, CheckCircle2, ArrowUpRight, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import ApprovalBoard from '../components/MediaApprovalBoard';
import HookLibrary from '../components/MediaHookLibrary';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL;

const MediaEngine = () => {
  const { token, user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [activeTab, setActiveTab] = useState('ideas');
  const [options, setOptions] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);

  const auth = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // ── Bootstrap: options + dashboard counts ───────────────────────────────
  useEffect(() => {
    if (!token) return;
    fetch(`${API}/api/media/options`, { headers: auth })
      .then(r => r.json()).then(setOptions).catch(() => {});
    refreshDashboard();
    // eslint-disable-next-line
  }, [token]);

  const refreshDashboard = useCallback(() => {
    fetch(`${API}/api/media/dashboard`, { headers: auth })
      .then(r => r.json()).then(setDashboard).catch(() => {});
  }, [auth]);

  // ── Global search ───────────────────────────────────────────────────────
  const runSearch = useCallback(async () => {
    const q = searchQuery.trim();
    if (!q) { setSearchResults(null); return; }
    try {
      const r = await fetch(`${API}/api/media/search?q=${encodeURIComponent(q)}`, { headers: auth });
      setSearchResults(await r.json());
    } catch (e) { toast.error('Search failed'); }
  }, [searchQuery, auth]);

  const tabs = [
    { key: 'ideas',    label: 'Content Ideas Bank', icon: Lightbulb },
    { key: 'board',    label: 'Approval Board',     icon: Layout },
    { key: 'hooks',    label: 'Hook & Script Library', icon: BookOpen },
    { key: 'requests', label: 'Marketing Requests', icon: Inbox },
  ];

  return (
    <div className="min-h-screen bg-page text-text-main pb-24 lg:pb-0" data-testid="media-engine-page">
      <Header />

      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-6 space-y-5">
        {/* Header strip */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-text-main">
              Media Engine
            </h1>
            <p className="text-sm text-text-muted mt-1">
              One place to collect ideas, approve content, build your hook library and triage marketing requests.
            </p>
          </div>
          {/* Search */}
          <div className="flex items-center gap-2 bg-surface border border-surface-border rounded-xl px-3 py-1.5 w-full sm:max-w-sm">
            <Search className="w-4 h-4 text-text-muted shrink-0" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && runSearch()}
              placeholder="Search across Media Engine…"
              className="flex-1 bg-transparent outline-none text-sm placeholder:text-text-muted"
              data-testid="media-search-input"
            />
            {searchQuery && (
              <button onClick={() => { setSearchQuery(''); setSearchResults(null); }} aria-label="Clear search">
                <X className="w-4 h-4 text-text-muted hover:text-text-main" />
              </button>
            )}
          </div>
        </div>

        {/* Dashboard strip */}
        <DashboardStrip dashboard={dashboard} onClickArea={(t) => setActiveTab(t)} />

        {/* Search results panel */}
        {searchResults && <SearchResults results={searchResults} onClose={() => { setSearchQuery(''); setSearchResults(null); }} />}

        {/* Tabs */}
        <div className="flex gap-1 overflow-x-auto border-b border-surface-border">
          {tabs.map(t => {
            const Icon = t.icon;
            const active = activeTab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                data-testid={`tab-${t.key}`}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors -mb-px ${
                  active
                    ? 'border-crm-blue text-crm-blue'
                    : 'border-transparent text-text-muted hover:text-text-main'
                }`}
              >
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Tab body */}
        <div className="pt-2">
          {activeTab === 'ideas' && (
            <IdeasBank auth={auth} options={options} isAdmin={isAdmin} onChange={refreshDashboard} />
          )}
          {activeTab === 'board' && (
            <ApprovalBoard auth={auth} options={options} isAdmin={isAdmin} onChange={refreshDashboard} />
          )}
          {activeTab === 'hooks' && (
            <HookLibrary auth={auth} options={options} isAdmin={isAdmin} onChange={refreshDashboard} />
          )}
          {activeTab === 'requests' && (
            <RequestsForm auth={auth} options={options} isAdmin={isAdmin} onChange={refreshDashboard} />
          )}
        </div>
      </div>
    </div>
  );
};

// ── DashboardStrip ─────────────────────────────────────────────────────────
const DashboardStrip = ({ dashboard, onClickArea }) => {
  if (!dashboard) {
    return <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
      {[1,2,3,4,5].map(i => <div key={i} className="h-20 rounded-xl bg-surface/60 border border-surface-border animate-pulse" />)}
    </div>;
  }
  const cards = [
    { label: 'New ideas',            value: dashboard.new_ideas,            color: 'bg-crm-blue/10 text-crm-blue',     tab: 'ideas',    test: 'kpi-new-ideas' },
    { label: 'Awaiting approval',    value: dashboard.awaiting_approval,    color: 'bg-crm-amber/15 text-crm-amber',   tab: 'board',    test: 'kpi-awaiting' },
    { label: 'Scheduled this week',  value: dashboard.scheduled_this_week,  color: 'bg-crm-teal/15 text-crm-teal',     tab: 'board',    test: 'kpi-scheduled' },
    { label: 'Requests pending',     value: dashboard.requests_pending,     color: 'bg-crm-red/10 text-crm-red',       tab: 'requests', test: 'kpi-requests' },
    { label: 'Posted this month',    value: dashboard.posted_this_month,    color: 'bg-emerald-500/10 text-emerald-600', tab: 'board',  test: 'kpi-posted' },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="media-dashboard-strip">
      {cards.map(c => (
        <button
          key={c.test}
          onClick={() => onClickArea?.(c.tab)}
          data-testid={c.test}
          className="text-left bg-surface border border-surface-border rounded-xl p-4 hover:shadow-md transition-all"
        >
          <p className={`inline-block text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${c.color}`}>
            {c.label}
          </p>
          <p className="text-2xl font-extrabold text-text-main mt-2 tabular-nums">{c.value}</p>
        </button>
      ))}
    </div>
  );
};

// ── SearchResults ──────────────────────────────────────────────────────────
const SearchResults = ({ results, onClose }) => {
  const total = (results.ideas?.length || 0) + (results.cards?.length || 0) + (results.hooks?.length || 0) + (results.requests?.length || 0);
  return (
    <div className="bg-surface border border-surface-border rounded-2xl p-4 shadow-soft" data-testid="media-search-results">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-bold uppercase tracking-widest text-text-muted">
          Search · {total} result{total === 1 ? '' : 's'}
        </p>
        <button onClick={onClose} className="text-xs text-text-muted hover:text-text-main">Clear</button>
      </div>
      {total === 0 ? (
        <p className="text-sm text-text-muted">No matches yet — try a shorter phrase.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {['ideas', 'cards', 'hooks', 'requests'].map(k => {
            const items = results[k] || [];
            if (!items.length) return null;
            return (
              <div key={k}>
                <p className="text-[10px] uppercase tracking-widest font-bold text-text-muted mb-1">{k}</p>
                <ul className="space-y-1">
                  {items.slice(0, 5).map(it => (
                    <li key={it.id} className="text-sm text-text-main truncate">• {it.title}</li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── ComingSoon placeholder ─────────────────────────────────────────────────
const ComingSoon = ({ title, detail, hint }) => (
  <div className="bg-surface border border-dashed border-surface-border rounded-2xl p-8 text-center" data-testid="media-coming-soon">
    <p className="text-xs uppercase tracking-widest font-bold text-crm-blue mb-2">Coming next</p>
    <h2 className="text-xl font-bold text-text-main mb-2">{title}</h2>
    <p className="text-sm text-text-muted max-w-lg mx-auto">{detail}</p>
    {hint && <p className="text-[11px] text-text-muted mt-3 italic">{hint}</p>}
  </div>
);

// ── 1. Content Ideas Bank ──────────────────────────────────────────────────
const IdeasBank = ({ auth, options, isAdmin, onChange }) => {
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [filters, setFilters] = useState({ category: '', status: '', priority: '' });

  const load = useCallback(async () => {
    setLoading(true);
    const qs = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && qs.append(k, v));
    const r = await fetch(`${API}/api/media/ideas?${qs}`, { headers: auth });
    setIdeas(await r.json());
    setLoading(false);
  }, [auth, filters]);

  useEffect(() => { load(); }, [load]);

  const handleStatusChange = async (id, status) => {
    const r = await fetch(`${API}/api/media/ideas/${id}`, {
      method: 'PATCH',
      headers: { ...auth, 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    if (r.ok) { toast.success(`Marked ${status}`); load(); onChange?.(); }
    else toast.error('Could not update — admin only');
  };

  const handlePromote = async (id) => {
    const r = await fetch(`${API}/api/media/ideas/${id}/promote-to-board`, {
      method: 'POST', headers: auth,
    });
    if (r.ok) { toast.success('Promoted to board'); load(); onChange?.(); }
    else toast.error('Failed to promote (admin only)');
  };

  const priorityBg = {
    Low: 'bg-slate-100 text-slate-600', Medium: 'bg-crm-blue/10 text-crm-blue', High: 'bg-crm-red/10 text-crm-red',
  };

  return (
    <div className="space-y-4" data-testid="ideas-bank">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex flex-wrap gap-2 flex-1">
          <Select value={filters.category || 'all'} onValueChange={v => setFilters(f => ({ ...f, category: v === 'all' ? '' : v }))}>
            <SelectTrigger className="w-44 h-9 text-xs"><SelectValue placeholder="All categories" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {options?.categories?.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.status || 'all'} onValueChange={v => setFilters(f => ({ ...f, status: v === 'all' ? '' : v }))}>
            <SelectTrigger className="w-44 h-9 text-xs"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {options?.idea_statuses?.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.priority || 'all'} onValueChange={v => setFilters(f => ({ ...f, priority: v === 'all' ? '' : v }))}>
            <SelectTrigger className="w-32 h-9 text-xs"><SelectValue placeholder="Priority" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All priorities</SelectItem>
              {['Low', 'Medium', 'High'].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-crm-blue hover:bg-crm-blue-light text-white" data-testid="new-idea-btn">
          <Plus className="w-4 h-4 mr-1" /> Submit idea
        </Button>
      </div>

      {showForm && (
        <IdeaForm auth={auth} options={options} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); onChange?.(); }} />
      )}

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-text-muted" /></div>
      ) : ideas.length === 0 ? (
        <div className="text-center py-12 text-text-muted">No ideas yet — submit the first one.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {ideas.map(idea => (
            <div key={idea.id} className="bg-surface border border-surface-border rounded-xl p-4 shadow-sm flex flex-col gap-2" data-testid={`idea-card-${idea.id}`}>
              <div className="flex items-start justify-between gap-2">
                <h3 className="text-sm font-bold text-text-main leading-tight">{idea.title}</h3>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full whitespace-nowrap ${priorityBg[idea.priority] || priorityBg.Medium}`}>{idea.priority}</span>
              </div>
              <p className="text-xs text-text-muted line-clamp-3">{idea.details || 'No details provided.'}</p>
              <div className="flex flex-wrap gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-text-muted">
                <span className="px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800">{idea.category}</span>
                <span className="px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800">{idea.content_format}</span>
                <span className="px-2 py-0.5 rounded-full bg-crm-teal/10 text-crm-teal">{idea.status}</span>
              </div>
              <div className="flex items-center justify-between text-[11px] text-text-muted pt-1 border-t border-surface-border mt-1">
                <span>{idea.submitted_by_name || '—'}</span>
                {isAdmin && (
                  <div className="flex items-center gap-1">
                    {idea.status === 'New idea' && (
                      <button onClick={() => handleStatusChange(idea.id, 'Approved')} className="px-2 py-0.5 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 rounded" title="Approve">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {['New idea', 'Approved', 'Under review'].includes(idea.status) && (
                      <button onClick={() => handlePromote(idea.id)} className="px-2 py-0.5 text-crm-blue hover:bg-crm-blue/10 rounded" title="Promote to board">
                        <ArrowUpRight className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const IdeaForm = ({ auth, options, onClose, onSaved }) => {
  const [f, setF] = useState({ title: '', category: '', content_format: '', details: '', priority: 'Medium', notes: '' });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.title.trim() || !f.category || !f.content_format) {
      toast.error('Title, category and format are required'); return;
    }
    setSaving(true);
    const r = await fetch(`${API}/api/media/ideas`, {
      method: 'POST', headers: { ...auth, 'Content-Type': 'application/json' }, body: JSON.stringify(f),
    });
    setSaving(false);
    if (r.ok) { toast.success('Idea submitted'); onSaved(); }
    else toast.error('Failed to submit');
  };
  return (
    <form onSubmit={submit} className="bg-surface border border-surface-border rounded-2xl p-4 shadow-soft space-y-3" data-testid="idea-form">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-bold text-text-main">Submit a content idea</h3>
        <button type="button" onClick={onClose} aria-label="Close"><X className="w-4 h-4 text-text-muted" /></button>
      </div>
      <Input placeholder='Idea title — e.g. "Why your bank said no but another lender might say yes"' value={f.title} onChange={e => setF({ ...f, title: e.target.value })} data-testid="idea-title" />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <Select value={f.category} onValueChange={v => setF({ ...f, category: v })}>
          <SelectTrigger data-testid="idea-category"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>{options?.categories?.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={f.content_format} onValueChange={v => setF({ ...f, content_format: v })}>
          <SelectTrigger data-testid="idea-format"><SelectValue placeholder="Format" /></SelectTrigger>
          <SelectContent>{options?.content_formats?.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={f.priority} onValueChange={v => setF({ ...f, priority: v })}>
          <SelectTrigger data-testid="idea-priority"><SelectValue /></SelectTrigger>
          <SelectContent>{['Low', 'Medium', 'High'].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <Textarea placeholder="Idea details — what's the angle, hook, or story?" value={f.details} onChange={e => setF({ ...f, details: e.target.value })} rows={3} data-testid="idea-details" />
      <Textarea placeholder="Notes for marketing (optional)" value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} rows={2} />
      <div className="flex items-center justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={saving} className="bg-crm-blue hover:bg-crm-blue-light text-white" data-testid="idea-submit">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit idea'}
        </Button>
      </div>
    </form>
  );
};

// ── 4. Marketing Request Form + list ───────────────────────────────────────
const RequestsForm = ({ auth, options, isAdmin, onChange }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await fetch(`${API}/api/media/requests`, { headers: auth });
    setRequests(await r.json());
    setLoading(false);
  }, [auth]);
  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    const r = await fetch(`${API}/api/media/requests/${id}`, {
      method: 'PATCH', headers: { ...auth, 'Content-Type': 'application/json' }, body: JSON.stringify({ status }),
    });
    if (r.ok) { toast.success('Updated'); load(); onChange?.(); }
  };

  const priorityBg = {
    Low: 'bg-slate-100 text-slate-600', Medium: 'bg-crm-blue/10 text-crm-blue',
    High: 'bg-crm-amber/15 text-crm-amber', Urgent: 'bg-crm-red/10 text-crm-red',
  };
  const statusBg = {
    pending: 'bg-crm-amber/15 text-crm-amber', in_progress: 'bg-crm-blue/10 text-crm-blue',
    completed: 'bg-emerald-500/10 text-emerald-600', cancelled: 'bg-slate-200 text-slate-600',
  };

  return (
    <div className="space-y-4" data-testid="requests-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-text-muted">Submit a request — it auto-creates a card on the marketing board.</p>
        <Button onClick={() => setShowForm(true)} className="bg-crm-blue hover:bg-crm-blue-light text-white" data-testid="new-request-btn">
          <Plus className="w-4 h-4 mr-1" /> New request
        </Button>
      </div>

      {showForm && (
        <RequestForm auth={auth} options={options} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); onChange?.(); }} />
      )}

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-text-muted" /></div>
      ) : requests.length === 0 ? (
        <div className="text-center py-12 text-text-muted">No requests yet.</div>
      ) : (
        <div className="space-y-2">
          {requests.map(req => (
            <div key={req.id} className="bg-surface border border-surface-border rounded-xl p-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`request-row-${req.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="text-sm font-bold text-text-main truncate">{req.title}</h4>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${priorityBg[req.priority] || priorityBg.Medium}`}>{req.priority}</span>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${statusBg[req.status] || statusBg.pending}`}>{req.status}</span>
                </div>
                <p className="text-xs text-text-muted mt-0.5 line-clamp-1">{req.purpose}</p>
                <p className="text-[11px] text-text-muted mt-0.5">
                  {req.request_type} · {req.requested_by_name}{req.deadline ? ` · due ${req.deadline}` : ''}
                </p>
              </div>
              {isAdmin && req.status !== 'completed' && (
                <div className="flex items-center gap-1.5 shrink-0">
                  {req.status === 'pending' && (
                    <Button size="sm" variant="outline" onClick={() => updateStatus(req.id, 'in_progress')}>Start</Button>
                  )}
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => updateStatus(req.id, 'completed')}>Complete</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const RequestForm = ({ auth, options, onClose, onSaved }) => {
  const [f, setF] = useState({
    title: '', request_type: '', purpose: '', target_audience: '',
    deadline: '', priority: 'Medium', additional_notes: '', supporting_files: [],
  });
  const [fileUrl, setFileUrl] = useState('');
  const [saving, setSaving] = useState(false);

  const addFile = () => {
    const u = fileUrl.trim();
    if (!u) return;
    setF(prev => ({ ...prev, supporting_files: [...prev.supporting_files, u] }));
    setFileUrl('');
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.title.trim() || !f.request_type || !f.purpose.trim()) {
      toast.error('Title, type and purpose are required'); return;
    }
    setSaving(true);
    const r = await fetch(`${API}/api/media/requests`, {
      method: 'POST', headers: { ...auth, 'Content-Type': 'application/json' }, body: JSON.stringify(f),
    });
    setSaving(false);
    if (r.ok) { toast.success('Request submitted — board card created'); onSaved(); }
    else toast.error('Failed to submit');
  };

  return (
    <form onSubmit={submit} className="bg-surface border border-surface-border rounded-2xl p-4 shadow-soft space-y-3" data-testid="request-form">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-bold text-text-main">New marketing request</h3>
        <button type="button" onClick={onClose} aria-label="Close"><X className="w-4 h-4 text-text-muted" /></button>
      </div>
      <Input placeholder='Request title — e.g. "Need flyer for First Home Buyer webinar"' value={f.title} onChange={e => setF({ ...f, title: e.target.value })} data-testid="request-title" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Select value={f.request_type} onValueChange={v => setF({ ...f, request_type: v })}>
          <SelectTrigger data-testid="request-type"><SelectValue placeholder="Request type" /></SelectTrigger>
          <SelectContent>{options?.request_types?.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={f.priority} onValueChange={v => setF({ ...f, priority: v })}>
          <SelectTrigger data-testid="request-priority"><SelectValue /></SelectTrigger>
          <SelectContent>{(options?.priorities || ['Low', 'Medium', 'High', 'Urgent']).map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <Textarea placeholder="What's the purpose of this request?" value={f.purpose} onChange={e => setF({ ...f, purpose: e.target.value })} rows={3} data-testid="request-purpose" />
      <Input placeholder="Target audience — e.g. first home buyers, investors" value={f.target_audience} onChange={e => setF({ ...f, target_audience: e.target.value })} />
      <Input type="date" placeholder="Deadline" value={f.deadline} onChange={e => setF({ ...f, deadline: e.target.value })} data-testid="request-deadline" />
      <div>
        <div className="flex gap-2">
          <Input placeholder="Paste a Canva / file link" value={fileUrl} onChange={e => setFileUrl(e.target.value)} />
          <Button type="button" variant="outline" onClick={addFile}>Add</Button>
        </div>
        {f.supporting_files.length > 0 && (
          <ul className="mt-2 text-xs text-text-muted space-y-0.5">
            {f.supporting_files.map((u, i) => <li key={i} className="truncate">↗ {u}</li>)}
          </ul>
        )}
      </div>
      <Textarea placeholder="Additional notes (optional)" value={f.additional_notes} onChange={e => setF({ ...f, additional_notes: e.target.value })} rows={2} />
      <div className="flex items-center justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={saving} className="bg-crm-blue hover:bg-crm-blue-light text-white" data-testid="request-submit">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit request'}
        </Button>
      </div>
    </form>
  );
};

// Re-export for the unused-import linter (icons referenced inside JSX above).
// eslint-disable-next-line no-unused-vars
const _unused = MessageSquare;

export default MediaEngine;
